import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import useSeo from '@/hooks/useSeo';
import { siteInfo } from '@/mocks/site';

const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const ABOUT_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于看料社',
    url: `${SITE_URL}/about`,
    description: '看料社的品牌定位、内容原则与联系方式。',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '看料社',
    url: SITE_URL,
    email: siteInfo.email,
    description: siteInfo.intro,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_URL}/about` },
    ],
  },
];

const principles = [
  {
    icon: 'ri-links-line',
    title: '只做整理，不做判断',
    desc: '我们把事件的公开信息按时间线归位，尽量还原过程，把结论留给读者自己。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '标注来源与时间',
    desc: '每条整理都注明对应的公开节点，方便你回溯核验，也方便我们在事实变化时修订。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新而非一次性',
    desc: '热门事件常有后续。专题上线后我们会继续补充，而不是发完就不再维护。',
  },
  {
    icon: 'ri-emotion-happy-line',
    title: '尊重当事人与读者',
    desc: '不做人身攻击，不放大未经证实的信息，避免让整理本身成为伤害的来源。',
  },
];

const steps = [
  { no: '01', title: '检索与收集', desc: '按关键词与时间范围收集公开信息片段。' },
  { no: '02', title: '交叉核对', desc: '对来源冲突的部分做二次核实，标注不确定项。' },
  { no: '03', title: '结构化归档', desc: '把碎片整理成时间线，拆分为可独立阅读的篇目。' },
  { no: '04', title: '持续维护', desc: '事件有新进展时补充内容，并在专题内更新记录。' },
];

const voices = [
  {
    name: '阿哲',
    role: '内容爱好者',
    text: '以前追一个热点要翻十几个帖子，现在看料社一个专题就把时间线理清楚了，省了太多时间。',
  },
  {
    name: '小满',
    role: '编辑从业者',
    text: '专题的结构很清晰，背景、节点、后续都分得开，我会拿它当素材整理的参考。',
  },
  {
    name: '老周',
    role: '行业观察者',
    text: '难得的是它不做情绪煽动，只把事实摆出来，这种克制在热点内容里很少见。',
  },
];

export default function About() {
  useSeo({
    title: '关于我们 - 看料社品牌介绍与内容原则',
    description:
      '了解看料社：一个专注在线看料专题整理的中文资源库。我们的品牌定位、内容原则、整理流程、用户口碑与联系方式都在这页。',
    keywords: '关于看料社,在线看料资源库,看料专题,内容整理原则,看料社联系方式',
    type: 'website',
    jsonLd: ABOUT_JSONLD,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="w-full bg-secondary-950 text-background-50">
          <div className="max-w-content mx-auto px-4 md:px-6 pt-24 md:pt-32 pb-12 md:pb-20">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="mt-6 font-heading text-3xl md:text-5xl font-bold leading-tight max-w-3xl">
              把热点整理清楚，
              <span className="text-accent-400">是看料社存在的理由</span>
            </h1>
            <p className="mt-6 max-w-3xl text-sm md:text-base text-background-200/90 leading-relaxed">
              {siteInfo.intro}
            </p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl">
              {[
                { v: '2021', l: '开始整理' },
                { v: '6 大', l: '主题分类' },
                { v: '每日', l: '持续更新' },
                { v: '100%', l: '人工核对' },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-accent-500/70 pl-4">
                  <p className="font-heading text-xl md:text-2xl font-bold">{s.v}</p>
                  <p className="text-xs md:text-sm text-background-200/80 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-content mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="w-full h-72 md:h-96 rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20scene%20with%20stacked%20notebooks%2C%20loose%20paper%20clippings%2C%20a%20magnifying%20glass%20and%20a%20cup%20of%20coffee%2C%20amber%20afternoon%20light%2C%20cream%20and%20burnt%20orange%20palette%2C%20paper%20grain%2C%20magazine%20photography%20aesthetic&width=1000&height=760&seq=kanliao-about-01&orientation=landscape"
                alt="看料社编辑部整理专题的工作场景"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] text-primary-600 font-medium mb-2 uppercase">
                Who we are
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                我们是一个专注整理的小团队
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                看料社从 {siteInfo.since} 年开始做热点整理。最初只是几个人在群里互相转发片段，后来发现大家真正缺的不是信息，而是把信息串起来的那根线。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                于是我们开始把零散的爆料、合集与系列内容归档成专题，给每起事件配上背景、节点和后续。今天你在资源库看到的每一个专题，都是这样一条一条整理出来的。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <span className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900">
                  人工核对
                </span>
                <span className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900">
                  标注来源
                </span>
                <span className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900">
                  持续维护
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-content mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <p className="text-xs tracking-[0.2em] text-secondary-600 font-medium mb-2 uppercase">
                Principles
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                我们坚持的四条内容原则
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {principles.map((p) => (
                <div
                  key={p.title}
                  className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 mb-4">
                    <i className={`${p.icon} text-xl`} />
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-content mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <p className="text-xs tracking-[0.2em] text-primary-600 font-medium mb-2 uppercase">
                Workflow
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                一个专题是怎么被整理出来的
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {steps.map((s) => (
                <div key={s.no} className="relative p-5 rounded-lg border border-background-200 bg-background-50">
                  <span className="font-heading text-3xl font-bold text-background-300">{s.no}</span>
                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{s.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-content mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <p className="text-xs tracking-[0.2em] text-secondary-600 font-medium mb-2 uppercase">
                Voices
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                读者的反馈
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {voices.map((v) => (
                <figure key={v.name} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                  <i className="ri-double-quotes-l text-2xl text-accent-500" />
                  <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed">
                    {v.text}
                  </blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-background-200 flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 font-heading font-bold">
                      {v.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm text-foreground-900">{v.name}</span>
                      <span className="block text-xs text-foreground-500">{v.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-content mx-auto px-4 md:px-6">
            <div className="rounded-lg border border-background-200 bg-secondary-950 text-background-50 p-8 md:p-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-2xl">
                <h2 className="font-heading text-2xl md:text-3xl font-bold">联系我们</h2>
                <p className="mt-3 text-sm md:text-base text-background-200/90 leading-relaxed">
                  内容纠错、转载授权或合作沟通，都欢迎写信给我们。我们会在 {siteInfo.workTime} 内回复。
                </p>
                <p className="mt-5 flex items-center gap-2 text-sm md:text-base">
                  <i className="ri-mail-send-line text-accent-400 text-lg" />
                  <a href={`mailto:${siteInfo.email}`} className="hover:text-accent-400 transition-colors">
                    {siteInfo.email}
                  </a>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link
                  to="/library"
                  className="px-6 py-3 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-400 transition-colors text-center whitespace-nowrap"
                >
                  浏览资源库
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-md border border-background-50/30 text-background-50 text-sm hover:bg-background-50/10 transition-colors text-center whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}